import { useEffect, useState } from 'preact/hooks';
import { back } from '@/state/nav';
import { db } from '@/lib/db';
import { Icon } from '@/components/Icon';
import type { ChatMessageRow } from '@/types';

interface DayGroup {
  day: string;
  rows: ChatMessageRow[];
}

/** Past conversations, grouped by day. Read-only. */
export function HistoryScreen() {
  const [groups, setGroups] = useState<DayGroup[]>([]);

  useEffect(() => {
    void db.messages
      .orderBy('created_at')
      .reverse()
      .limit(300)
      .toArray()
      .then((rows) => {
        const byDay = new Map<string, ChatMessageRow[]>();
        for (const r of rows) {
          if (!byDay.has(r.day)) byDay.set(r.day, []);
          byDay.get(r.day)!.push(r);
        }
        setGroups(
          [...byDay.entries()]
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([day, rs]) => ({ day, rows: rs.reverse() }))
        );
      });
  }, []);

  const fmtDay = (d: string) => {
    const today = new Date().toISOString().slice(0, 10);
    if (d === today) return 'Today';
    return new Date(d + 'T12:00').toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
  };

  return (
    <div class="screen screen--no-tabbar screen--fade-in">
      <header class="header header--sub">
        <button class="iconbtn" aria-label="Back" onClick={() => back('/')}>
          <Icon name="chevron-left" size={20} />
        </button>
        <div>
          <div class="header__title">History</div>
        </div>
      </header>
      <div class="scroll" style={{ gap: '14px' }}>
        {groups.length === 0 && <p class="roster-empty">No conversations yet.</p>}
        {groups.map((g) => (
          <div key={g.day}>
            <p class="t-eyebrow" style={{ margin: '10px 4px 8px' }}>{fmtDay(g.day)}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {g.rows.map((r) => {
                const c = r.content as { text?: string; proposal?: { summary: string }; proposalStatus?: string };
                return (
                  <div key={r.id} class={`bub ${r.role === 'user' ? 'bub--u' : 'bub--a'}`} style={{ animation: 'none' }}>
                    {r.role === 'assistant' && <span class="mark">T</span>}
                    <div>
                      {c.text}
                      {c.proposal && (
                        <div class="t-meta" style={{ marginTop: '4px' }}>
                          ▸ {c.proposal.summary}
                          {c.proposalStatus === 'applied' ? ' · applied' : c.proposalStatus === 'dismissed' ? ' · not applied' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
